import { Decimal, isValidEmail, isValidTckn, isValidVkn, Money } from "@station/core";
import { z } from "zod";
import { CREDIT_TENDER_TYPES, defaultUnitPriceScale, lineGross, saleLineKindSchema, tenderTypeSchema, unitPriceScaleSchema, } from "./types.js";
/**
 * `POST /v1/sales` gövdesinin makine okunur karşılığı (`docs/domain/pos-local-api.md` §3) ve ajanın yaptığı
 * denetimin TS EŞLENİĞİ (`verifyBasket`). Go ajanı aynı kuralları uygular; ayrışma olursa belge düzeltilir.
 */
const kurus = z.string().regex(/^-?\d{1,19}$/, "Tutar kuruş tam sayı dizgesi olmalıdır");
const nonNegativeKurus = kurus.refine((value) => !value.startsWith("-"), "Tutar negatif olamaz");
const quantity = z.string().regex(/^\d{1,15}(\.\d{1,3})?$/, "Miktar en çok 3 ondalık");
const unitPrice = z.string().regex(/^\d{1,15}(\.\d{1,4})?$/, "Birim fiyat en çok 4 ondalık");
export const posSaleLineSchema = z.object({
    lineNo: z.number().int().positive(),
    kind: saleLineKindSchema,
    /** `FUEL` satırında ZORUNLU: bekleyen dolum işleminin kimliği. */
    fuelAgentTxId: z.string().min(1).nullish(),
    productId: z.uuid(),
    barcode: z.string().min(1).nullish(),
    quantity,
    unitPrice,
    /** Belge iki ölçek kullanır; belirsizliği gidermek için satır isteğe bağlı olarak ölçeğini bildirir. */
    unitPriceScale: unitPriceScaleSchema.optional(),
    gross: nonNegativeKurus,
    vatRateCode: z.string().min(1),
    /** İsteğe bağlı: ajan oranı ana veriden de çözebilir. */
    vatRatePercent: z.string().optional(),
    ageRestricted: z.boolean().optional(),
    /** Prompt 17: abonelik hakkıyla sıfırlanan satır (indirimi `ABONELIK:<plan>`; pos-local-api §7.2 kural 4). */
    subscriptionId: z.uuid().nullish(),
});
export const posSaleDiscountSchema = z.object({
    lineNo: z.number().int().positive(),
    campaignId: z.uuid().nullable(),
    amount: nonNegativeKurus,
    reason: z.string().min(1).max(120),
});
export const posSaleCreditCheckSchema = z.object({
    verdict: z.enum(["ALLOWED", "TOLERANCE", "BLOCKED", "LIMIT_EXCEEDED"]),
    source: z.literal("CACHE"),
    availableBefore: kurus.nullable(),
    overridden: z.boolean().optional(),
});
export const posSaleTenderSchema = z.object({
    type: tenderTypeSchema,
    amount: nonNegativeKurus,
    changeGiven: nonNegativeKurus.optional(),
    posTerminalId: z.uuid().nullish(),
    slipNo: z.string().max(40).nullish(),
    counterpartyId: z.uuid().nullish(),
    creditCheck: posSaleCreditCheckSchema.nullish(),
    /** Prompt 17: `LOYALTY_POINTS` ödemesinde harcanan puan (tam sayı dizge). */
    points: z
        .string()
        .regex(/^\d{1,15}$/, "Puan tam sayı dizge olmalıdır")
        .nullish(),
});
export const CUSTOMER_IDENTIFIED_BY = [
    "CARD",
    "QR",
    "PLATE",
    "TAG",
    "DRIVER_CARD",
    "PHONE",
    "MANUAL",
];
export const customerIdentifiedBySchema = z.enum(CUSTOMER_IDENTIFIED_BY);
export const posSaleCustomerSchema = z.object({
    counterpartyId: z.uuid().nullable(),
    vehiclePlate: z.string().max(20).nullable(),
    phone: z.string().max(24).nullable(),
    loyaltyCard: z.string().max(64).nullable(),
    // ── Prompt 17 (pos-local-api §7.2) — hepsi isteğe bağlı; eski POS göndermez ──
    customerProfileId: z.uuid().nullish(),
    vehicleId: z.uuid().nullish(),
    fleetDriverId: z.uuid().nullish(),
    /** Tam sayı km. */
    odometerKm: z.number().int().nonnegative().max(9_999_999).nullish(),
    /** Kampanya hesabında KULLANILAN sadakat seviyesi (denetim izi). */
    loyaltyTier: z.string().max(40).nullish(),
    identifiedBy: customerIdentifiedBySchema.nullish(),
});
/** Prompt 17: satışta okutulan kupon (indirim satırı `campaignId` ile bağlanır). */
export const posSaleCouponSchema = z.object({
    couponId: z.uuid(),
    code: z.string().min(1).max(40),
    campaignId: z.uuid(),
});
/** Fatura talebi: VKN 10 hane ya da TCKN 11 hane (AGENTS.md §10). */
export const posInvoiceRequestSchema = z.object({
    buyerTaxId: z
        .string()
        .trim()
        .refine((value) => isValidVkn(value) || isValidTckn(value), "VKN 10, TCKN 11 haneli ve geçerli olmalıdır"),
    buyerName: z.string().trim().min(2).max(200),
    buyerTaxOffice: z.string().trim().max(120).nullish(),
    buyerAddress: z.string().trim().max(400).nullish(),
    buyerEmail: z
        .string()
        .trim()
        .refine((value) => value === "" || isValidEmail(value), "Geçerli bir e-posta adresi girin")
        .nullish(),
});
export const posSupervisorSchema = z.object({
    staffCode: z.string().min(1).max(20),
    pin: z.string().min(4).max(12),
});
export const posSaleRequestSchema = z.object({
    agentSaleId: z.uuid(),
    occurredAt: z.iso.datetime({ offset: true }),
    cashRegisterId: z.uuid().nullable(),
    lines: z.array(posSaleLineSchema).min(1),
    discounts: z.array(posSaleDiscountSchema),
    tenders: z.array(posSaleTenderSchema).min(1),
    customer: posSaleCustomerSchema,
    coupons: z.array(posSaleCouponSchema).max(20).optional(),
    invoiceRequest: posInvoiceRequestSchema.nullable(),
    ageConfirmation: z
        .object({ confirmedByStaffId: z.uuid(), at: z.iso.datetime({ offset: true }) })
        .nullable(),
    supervisor: posSupervisorSchema.nullable(),
    note: z.string().max(500).nullable(),
});
export const posReceiptSchema = z.object({
    fiscalDeviceSerial: z.string(),
    zNo: z.number().int(),
    receiptNo: z.number().int(),
    receiptRef: z.string(),
    printedAt: z.iso.datetime({ offset: true }),
});
export const posSaleResponseSchema = z.object({
    agentSaleId: z.uuid(),
    receipt: posReceiptSchema.nullable(),
    totals: z.object({
        gross: kurus,
        discount: kurus,
        net: kurus,
        vat: z.array(z.object({ rate: z.string(), base: kurus, vat: kurus })),
    }),
    sync: z.enum(["queued", "acked", "rejected"]),
    warnings: z.array(z.string()).default([]),
});
/**
 * Ajanın yaptığı denetimin TS eşleniği (pos-local-api §3, kural 1):
 *  - her satırda `gross = ROUND_HALF_UP(quantity × unitPrice)`
 *  - `Σ gross − Σ discount = Σ tenders − Σ changeGiven`
 *  - indirim satır brütünü aşamaz, bilinmeyen satıra bağlanamaz
 *  - `FUEL` satırı bir bekleyen işleme bağlı olmalıdır
 *  - yaş sınırlı satır varsa `ageConfirmation` zorunludur
 */
export function verifyBasket(body) {
    const problems = [];
    const grossByLine = new Map();
    for (const line of body.lines) {
        const scale = line.unitPriceScale ?? defaultUnitPriceScale(line.kind);
        const expected = lineGross(line.unitPrice, line.quantity, scale);
        const declared = Money.fromMinor(line.gross);
        if (!expected.eq(declared)) {
            problems.push({
                code: "POS_LINE_GROSS_MISMATCH",
                message: `Satır ${line.lineNo}: tutar ${declared.formatTr()} beklenen ${expected.formatTr()} ile uyuşmuyor.`,
                meta: { lineNo: String(line.lineNo), expected: expected.toString(), declared: declared.toString() },
            });
        }
        grossByLine.set(line.lineNo, declared);
        if (line.kind === "FUEL" && !line.fuelAgentTxId) {
            problems.push({
                code: "POS_FUEL_LINE_INVALID",
                message: `Satır ${line.lineNo}: akaryakıt satırı bekleyen bir dolum işlemine bağlı değil.`,
                meta: { lineNo: String(line.lineNo) },
            });
        }
    }
    const discountByLine = new Map();
    for (const discount of body.discounts) {
        const gross = grossByLine.get(discount.lineNo);
        if (!gross) {
            problems.push({
                code: "POS_DISCOUNT_INVALID",
                message: `İndirim bilinmeyen satıra bağlı: ${discount.lineNo}`,
                meta: { lineNo: String(discount.lineNo) },
            });
            continue;
        }
        discountByLine.set(discount.lineNo, (discountByLine.get(discount.lineNo) ?? Money.ZERO).add(Money.fromMinor(discount.amount)));
    }
    for (const [lineNo, amount] of discountByLine) {
        if (amount.gt(grossByLine.get(lineNo))) {
            problems.push({
                code: "POS_DISCOUNT_INVALID",
                message: `Satır ${lineNo}: indirim satır tutarını aşıyor.`,
                meta: { lineNo: String(lineNo) },
            });
        }
    }
    const gross = Money.sum([...grossByLine.values()]);
    const discount = Money.sum([...discountByLine.values()]);
    const due = gross.sub(discount);
    const tendered = Money.sum(body.tenders.map((tender) => Money.fromMinor(tender.amount)));
    const change = Money.sum(body.tenders.map((tender) => Money.fromMinor(tender.changeGiven ?? "0")));
    // Para üstü yalnızca nakitten verilebilir.
    for (const tender of body.tenders) {
        if (tender.type !== "CASH" && tender.changeGiven && !Money.fromMinor(tender.changeGiven).isZero()) {
            problems.push({
                code: "POS_TENDER_INVALID",
                message: "Para üstü yalnızca nakit tahsilatında olabilir.",
                meta: { type: tender.type },
            });
        }
    }
    if (!due.eq(tendered.sub(change))) {
        problems.push({
            code: "POS_TOTALS_MISMATCH",
            message: `Tahsilat toplamı tutmuyor: ${due.formatTr()} ≠ ${tendered.sub(change).formatTr()}`,
            meta: { due: due.toString(), tendered: tendered.toString(), changeGiven: change.toString() },
        });
    }
    if (body.lines.some((line) => line.ageRestricted === true) && !body.ageConfirmation) {
        problems.push({
            code: "POS_AGE_CONFIRMATION_REQUIRED",
            message: "Yaş sınırlı ürün var; kasiyer onayı gerekir.",
        });
    }
    return {
        ok: problems.length === 0,
        problems,
        computed: {
            gross: gross.toString(),
            discount: discount.toString(),
            due: due.toString(),
            tendered: tendered.toString(),
            changeGiven: change.toString(),
        },
    };
}
/**
 * `computeBasket` çıktısını DOĞRUDAN `POST /v1/sales` gövdesine çevirir — motorun sözleşmeyle
 * tek temas noktası burasıdır, POS başka yerde gövde kurmaz.
 */
export function buildSaleRequest(basket, lines, meta) {
    const extra = new Map(lines.map((line) => [line.lineNo, line]));
    return {
        agentSaleId: meta.agentSaleId,
        occurredAt: typeof meta.occurredAt === "string" ? meta.occurredAt : meta.occurredAt.toISOString(),
        cashRegisterId: meta.cashRegisterId,
        lines: basket.lines.map((line) => {
            const source = extra.get(line.lineNo);
            return {
                lineNo: line.lineNo,
                kind: line.kind,
                fuelAgentTxId: source?.fuelAgentTxId ?? null,
                productId: line.productId,
                barcode: source?.barcode ?? null,
                quantity: Decimal.from(line.quantity).toFixed(3),
                unitPrice: line.unitPrice,
                ...(source?.unitPriceScale ? { unitPriceScale: source.unitPriceScale } : {}),
                gross: line.gross,
                vatRateCode: line.vatRateCode,
                ...(meta.includeVatRatePercent ? { vatRatePercent: line.vatRatePercent } : {}),
                ageRestricted: source?.ageRestricted ?? false,
                ...(source?.subscriptionId ? { subscriptionId: source.subscriptionId } : {}),
            };
        }),
        discounts: basket.discounts.map((discount) => ({
            lineNo: discount.lineNo,
            campaignId: discount.campaignId,
            amount: discount.amount,
            reason: discount.reason,
        })),
        tenders: basket.tenders.map((tender) => ({
            type: tender.type,
            amount: tender.amount,
            changeGiven: tender.changeGiven,
            posTerminalId: tender.posTerminalId ?? null,
            slipNo: tender.slipNo ?? null,
            counterpartyId: tender.counterpartyId ?? null,
            ...(tender.type === "LOYALTY_POINTS" && tender.points ? { points: tender.points } : {}),
            creditCheck: basket.credit && CREDIT_TENDER_TYPES.includes(tender.type)
                ? {
                    verdict: basket.credit.verdict,
                    source: "CACHE",
                    availableBefore: basket.credit.availableBefore,
                    overridden: basket.credit.requiresSupervisor && meta.supervisor != null,
                }
                : null,
        })),
        customer: meta.customer ?? {
            counterpartyId: null,
            vehiclePlate: null,
            phone: null,
            loyaltyCard: null,
        },
        ...(meta.coupons && meta.coupons.length > 0 ? { coupons: [...meta.coupons] } : {}),
        invoiceRequest: meta.invoiceRequest ?? null,
        ageConfirmation: meta.ageConfirmation ?? null,
        supervisor: meta.supervisor ?? null,
        note: meta.note ?? null,
    };
}
//# sourceMappingURL=sale.js.map